import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Field, Section, NativeSelect } from "../common";
import { api, errMsg } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";

export default function CommercialTab({ q, d, set, m, locked, reload }) {
  const { t } = useT();
  const { can } = useAuth();
  const canFx = can("fx:write");
  const plants = (m.customer?.plants || []).map((p) => ({ value: p.id, label: `${p.name} – ${p.city || ""}` }));
  const contacts = (m.customer?.contacts || []).map((c) => ({ value: c.id, label: `${c.name}${c.role ? " · " + c.role : ""}` }));
  const refreshRate = async () => {
    try { await api.post(`/quotations/${q.id}/refresh-rate`); await reload(); toast.success(t("common.saved")); } catch (e) { toast.error(errMsg(e)); }
  };
  const changeCustomer = (v) => {
    if (!v || v === d.customer_id) return;
    const c = (m.customers || []).find((x) => x.id === v);
    set({ customer_id: v, country: c?.country || d.country, currency: q.id ? d.currency : (c?.country === "MX" ? "MXN" : "USD"),
      plant_id: null, ship_to_plant_id: null, contact_id: null, machine_ids: [], tax_rule_ids: [] });
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
      <Section title={t("q.customer")} testid="section-customer">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("q.customer")} required className="sm:col-span-2">
            <NativeSelect testid="customer-select" disabled={locked} value={d.customer_id} placeholder={d.customer_id ? undefined : "—"} onChange={changeCustomer}
              options={(m.customers || []).map((c) => ({ value: c.id, label: `${c.name} (${c.country})` }))} />
          </Field>
          <Field label={t("ws.plant")}>
            <NativeSelect testid="plant-select" disabled={locked} value={d.plant_id} placeholder="—" onChange={(v) => set({ plant_id: v })} options={plants} />
          </Field>
          <Field label={t("ws.shipTo")}>
            <NativeSelect testid="shipto-select" disabled={locked} value={d.ship_to_plant_id} placeholder="—" onChange={(v) => set({ ship_to_plant_id: v })} options={plants} />
          </Field>
          <Field label={t("ws.contact")}>
            <NativeSelect testid="contact-select" disabled={locked} value={d.contact_id} placeholder="—" onChange={(v) => set({ contact_id: v })} options={contacts} />
          </Field>
          <Field label={t("ws.salesPerson")}>
            <NativeSelect testid="salesperson-select" disabled={locked} value={d.sales_person_id} placeholder="—" onChange={(v) => set({ sales_person_id: v })}
              options={(m.users || []).map((u) => ({ value: u.id, label: u.name }))} />
          </Field>
          <Field label={t("q.subject")} className="sm:col-span-2">
            <Input data-testid="title-input" disabled={locked} value={d.title || ""} onChange={(e) => set({ title: e.target.value })} />
          </Field>
        </div>
      </Section>
      <Section title={t("ws.commercial")} testid="section-commercial">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("q.currency")} required>
            <NativeSelect testid="currency-select" disabled={locked} value={d.currency} onChange={(v) => set({ currency: v })}
              options={[{ value: "USD", label: "USD" }, { value: "MXN", label: "MXN" }]} />
          </Field>
          <Field label={t("ws.rate")} required>
            <div className="flex gap-2">
              <Input data-testid="exchange-rate-input" type="number" step="0.0001" disabled={locked || !canFx} title={canFx ? "" : t("ws.fxAdminOnly")} value={d.exchange_rate ?? ""}
                onChange={(e) => set({ exchange_rate: e.target.value === "" ? null : Number(e.target.value) })} className="font-mono" />
              {!locked && q.id && <Button type="button" variant="outline" size="icon" title={t("ws.refreshRate")} data-testid="refresh-rate-btn" onClick={refreshRate} className="shrink-0"><RefreshCw className="h-4 w-4" /></Button>}
            </div>
          </Field>
          <Field label={t("ws.quoteDate")} required>
            <Input data-testid="quote-date-input" type="date" disabled={locked} value={d.quote_date || ""} onChange={(e) => set({ quote_date: e.target.value })} />
          </Field>
          <Field label={t("ws.validUntil")} required>
            <Input data-testid="valid-until-input" type="date" disabled={locked} value={d.valid_until || ""} onChange={(e) => set({ valid_until: e.target.value })} />
          </Field>
          <Field label={t("ws.terms")} className="sm:col-span-2">
            <Input data-testid="terms-input" disabled={locked} value={d.terms || ""} onChange={(e) => set({ terms: e.target.value })} />
          </Field>
          <Field label={t("ws.remarks")} className="sm:col-span-2">
            <Textarea data-testid="remarks-input" rows={3} disabled={locked} value={d.remarks || ""} onChange={(e) => set({ remarks: e.target.value })} />
          </Field>
        </div>
      </Section>
    </div>
  );
}
